import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { ChevronDown, User, Eye, Home } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function ViewSwitchHeader() {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState('admin');
  const [previewEmployee, setPreviewEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (user?.role === 'administrator') {
      setActiveView(user.active_view || 'admin');
      if (user.active_view === 'preview_employee' && user.preview_employee_id) {
        fetchPreviewEmployee(user.preview_employee_id);
      }
      if (user.active_view === 'self_employee') {
        fetchSelfEmployee();
      }
      fetchEmployees();
    }
  }, [user]);

  const fetchEmployees = async () => {
    if (user?.active_view === 'preview_employee' || user?.active_view === 'self_employee') return; // /api/users is admin-only
    try {
      const res = await fetch(`${API}/api/users`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Every previewable person = all non-administrator accounts (managers, trainees,
        // receptionists, contractors, content creators, archived — all of them).
        const nonAdmin = (data || []).filter(u => u.role !== 'administrator');
        setEmployees(nonAdmin);
      }
    } catch (err) {
      // Non-critical: request may be aborted by StrictMode remount; a later fetch repopulates.
      console.debug('Preview employee list fetch skipped/failed:', err);
    }
  };

  const fetchPreviewEmployee = async (empId) => {
    try {
      const res = await fetch(`${API}/api/users/${empId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPreviewEmployee(data);
      }
    } catch (err) {
      console.error('Failed to fetch preview employee');
    }
  };

  const fetchSelfEmployee = async () => {
    try {
      const res = await fetch(`${API}/api/users/${user._id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPreviewEmployee(data);
      }
    } catch (err) {
      console.error('Failed to fetch self employee');
    }
  };

  const switchView = async (view, employeeId = null) => {
    try {
      const res = await fetch(`${API}/api/auth/switch-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          view,
          preview_employee_id: employeeId
        })
      });

      if (res.ok) {
        if (view === 'admin' && window.location.pathname.startsWith('/employee/')) {
          window.location.assign('/');
        } else {
          window.location.reload();
        }
      }
    } catch (err) {
      console.error('Failed to switch view');
    }
  };

  if (user?.role !== 'administrator') return null;

  // Sticky notification bar for non-admin views
  if (activeView === 'self_employee') {
    return (
      <div className="sticky top-0 z-50 bg-[#9C6B4A] text-white px-4 py-2 flex items-center justify-between shadow-md">
        <span className="text-sm">You're in your employee view. Switch back to admin.</span>
        <button
          onClick={() => switchView('admin')}
          className="text-xs px-3 py-1 bg-white/20 hover:bg-white/30 rounded transition"
        >
          Exit to Admin
        </button>
      </div>
    );
  }

  if (activeView === 'preview_employee' && previewEmployee) {
    const subjectNeedsPassword = previewEmployee.requires_password_change || previewEmployee.must_change_password;
    const subjectName = previewEmployee.display_name || previewEmployee.name?.split(' ')[0] || 'Employee';
    return (
      <div className="sticky top-0 z-50 bg-[#231E1A] text-white px-4 py-2 flex items-center justify-between shadow-md" data-testid="preview-banner">
        <span className="text-sm flex items-center gap-2 flex-wrap">
          Read-only preview of {subjectName}'s view. Nothing you do here is saved.
          {subjectNeedsPassword && (
            <span className="text-xs px-2 py-0.5 bg-white/15 rounded-full" data-testid="preview-password-chip">
              {subjectName} hasn't set their password yet
            </span>
          )}
        </span>
        <button
          onClick={() => switchView('admin')}
          className="text-xs px-3 py-1 bg-white/20 hover:bg-white/30 rounded transition"
          data-testid="exit-preview-button"
        >
          Exit Preview
        </button>
      </div>
    );
  }

  // Admin view: dropdown in header
  return (
    <div className="border-b border-stone-200 bg-white sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-stone-600">View:</span>
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2"
            >
              {activeView === 'admin' && (
                <>
                  <Home className="h-4 w-4" />
                  <span>Admin</span>
                </>
              )}
              {activeView === 'self_employee' && (
                <>
                  <User className="h-4 w-4" />
                  <span>My Employee View</span>
                </>
              )}
              {activeView === 'preview_employee' && (
                <>
                  <Eye className="h-4 w-4" />
                  <span>Preview: {previewEmployee?.display_name || 'Loading...'}</span>
                </>
              )}
              <ChevronDown className="h-4 w-4" />
            </Button>

            {showDropdown && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-stone-200 rounded-md shadow-lg z-50 min-w-[200px]">
                <button
                  onClick={() => {
                    switchView('admin');
                    setShowDropdown(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2"
                >
                  <Home className="h-4 w-4" />
                  Admin View
                </button>
                <button
                  onClick={() => {
                    switchView('self_employee');
                    setShowDropdown(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2"
                >
                  <User className="h-4 w-4" />
                  My Employee View
                </button>
                <div className="border-t border-stone-200 my-1"></div>
                {(() => {
                  const roleLabel = (u) => u.role === 'manager' ? 'Manager' : (u.employment_role || 'Team');
                  const sorted = [...employees].sort((a, b) =>
                    (a.display_name || a.name || '').localeCompare(b.display_name || b.name || ''));
                  const activeTeam = sorted.filter(u => u.is_active !== false);
                  const archived = sorted.filter(u => u.is_active === false);
                  const row = (emp, faint) => (
                    <button
                      key={emp._id}
                      data-testid={`preview-employee-${emp._id}`}
                      onClick={() => { switchView('preview_employee', emp._id); setShowDropdown(false); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2 ${faint ? 'opacity-60' : ''}`}
                    >
                      <Eye className="h-4 w-4 flex-shrink-0" />
                      <span className="flex-1">{emp.display_name || emp.name}</span>
                      <span className="text-xs text-stone-400">{roleLabel(emp)}</span>
                    </button>
                  );
                  return (
                    <>
                      <div className="px-4 py-2 text-xs text-stone-500 font-medium" data-testid="preview-group-active">Active team</div>
                      {activeTeam.map(emp => row(emp, false))}
                      {archived.length > 0 && (
                        <>
                          <div className="px-4 py-2 text-xs text-stone-400 font-medium" data-testid="preview-group-archived">Archived</div>
                          {archived.map(emp => row(emp, true))}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {activeView !== 'admin' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => switchView('admin')}
          >
            Exit to Admin
          </Button>
        )}
      </div>

      {activeView === 'self_employee' && (
        <div className="bg-amber-600 text-white px-4 py-2 text-sm font-medium sticky top-0 z-40">
          <div className="max-w-7xl mx-auto flex items-center gap-2">
            <User className="h-4 w-4" />
            <span>You are viewing your own employee dashboard</span>
          </div>
        </div>
      )}

      {activeView === 'preview_employee' && (
        <div className="bg-stone-800 text-white px-4 py-2 text-sm font-medium sticky top-0 z-40">
          <div className="max-w-7xl mx-auto flex items-center gap-2">
            <Eye className="h-4 w-4" />
            <span>Read-only preview of {previewEmployee?.display_name || 'employee'} dashboard</span>
          </div>
        </div>
      )}
    </div>
  );
}
